import classNames from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { Outlet, matchPath, useLocation } from "react-router-dom";

import Footer from "@components/organisms/Footer";
import Modal from "@components/organisms/Modal";

import Cart from "@organisms/Cart";
import Header from "@organisms/Header";
import Search from "@organisms/Header/Search";
import LoginModal from "@organisms/Login/LoginModal";
import Toast from "@organisms/Toast";

import useLayout from "@hooks/reducerHooks/useLayout";
import useMeta from "@hooks/reducerHooks/useMeta";
import useCustomTheme, { pageCustomTheme } from "@hooks/useCustomTheme";
import useVhResize from "@hooks/useVhResize";

import { URLS } from "@const/urls";

function PageLayout({ isSaleShutDown }: { isSaleShutDown: boolean }) {
  const { isCartOpen, isLoginOpen, isModalOpen, isHeaderOpen, isSearchOpen, isToastOpen, isShowFooter } = useLayout();
  const [isNoticeOpen, setIsNoticeOpen] = useState(true);

  const { changeTheme } = useCustomTheme();
  const { meta, clearMetaTag } = useMeta();

  useVhResize();

  const location = useLocation();
  const currentPathname = location.pathname;

  const theme = useMemo(() => {
    const pageName = location.pathname.replace("/", "");
    return changeTheme(pageCustomTheme[pageName]);
  }, [location]);

  const statusBar = useMemo(() => {
    const pageName = location.pathname.replace("/", "");

    if (isCartOpen || isSearchOpen) {
      return "white";
    } else if (isHeaderOpen) {
      return `rgb(${theme.bgSecondary})`;
    } else if (pageName.includes("tekken8")) {
      return `black`;
    }

    return `rgb(${theme.bgPrimary})`;
  }, [isCartOpen, isSearchOpen, isHeaderOpen]);

  useEffect(() => {
    return () => {
      clearMetaTag();
    };
  }, [location]);

  return (
    <div className="relative">
      <HelmetProvider>
        <Helmet>
          {Boolean(meta.title) && <title>{meta.title}</title>}
          {Boolean(meta.imageSrc) && <meta content={meta.imageSrc} name="og:image" />}
          {theme ? <meta content={statusBar} name="theme-color" /> : <meta content="white" name="theme-color" />}
        </Helmet>
      </HelmetProvider>
      <Header isSaleShutDown={isSaleShutDown} />
      <main
        className={classNames(
          "pc:min-h-[calc(100vh_-_362px)] pc:grid-cols-12 pc:gap-x-5 pc:px-5 relative mt-5 grid min-h-[75vh] auto-rows-min grid-cols-6 gap-x-2.5 gap-y-10 px-4",
        )}
      >
        <Outlet />
      </main>
      {!isSaleShutDown && isCartOpen ? <Cart /> : null}
      {isLoginOpen ? <LoginModal /> : null}
      {isModalOpen ? <Modal /> : null}
      {isSearchOpen ? <Search /> : null}
      {isShowFooter ? <Footer /> : null}
      {isToastOpen ? <Toast /> : null}
      <Toast />
      <div
        className={classNames(
          "bg-tertiary pc:bottom-5 pc:left-5 pc:max-w-lg fixed bottom-4 left-4 z-20 w-[calc(100%-2rem)] rounded p-5",
          isNoticeOpen ? "block" : "hidden",
        )}
      >
        <div className="flex justify-between">
          <span>아카이브 세일 종료 안내</span>
          <button
            onClick={() => {
              setIsNoticeOpen(false);
            }}
          >
            닫기
          </button>
        </div>
        <br />
        <p>
          • 이번 시즌 아카이브 세일이 종료되었습니다.
          <br />
          • 문의사항은 이메일과 라이브챗을 통해 접수해 주시기 바랍니다.
          <br />
          <a className="underline" href="mailto:service@tune.kr">
            service@tune.kr
          </a>
        </p>
      </div>
    </div>
  );
}

export default PageLayout;
